import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Home, Crosshair, Send } from "lucide-react";
import DoseCalculator from "./DoseCalculator";
import Cursor from "./Cursor";
import Slash from "./Slash";
import { buzz } from "../lib/haptics";

const EASE = [0.16, 1, 0.3, 1];

export default function LabPage() {
  const navigate = useNavigate();
  const [wiping, setWiping] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Toji Peptides — Reconstitution Calculator";
  }, []);

  const leaveTo = (target) => {
    if (wiping) return;
    buzz(14);
    if (target) sessionStorage.setItem("toji_scroll_target", target);
    setWiping(true);
  };

  return (
    <div className="App grain sumi relative min-h-screen overflow-hidden bg-[#050505] text-white">
      <Cursor />

      <motion.header
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: EASE }}
        className="sticky top-0 z-40 border-b border-[#1c1c1c] bg-black/70 backdrop-blur-xl"
      >
        <div className="mx-auto flex max-w-[1100px] items-center justify-between px-6 py-4 md:px-12">
          <button
            onClick={() => leaveTo(null)}
            data-testid="lab-back"
            className="slash-host group flex items-center gap-2 font-mono2 text-xs uppercase tracking-[0.2em] text-white/70 transition-colors hover:text-[#d91616]"
          >
            <ArrowLeft size={16} className="transition-transform group-hover:-translate-x-1" />
            Back to Toji
            <Slash />
          </button>
          <span className="flex items-center gap-2 font-display text-lg font-extrabold tracking-tight">
            <span className="font-jp text-[#d91616]">藤</span>
            TOJI<span className="text-[#d91616]">.</span>
            <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-white/40">Lab</span>
          </span>
        </div>
      </motion.header>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
      >
        <DoseCalculator />
      </motion.div>

      {/* Rail navigation */}
      <nav
        data-testid="lab-rail"
        aria-label="Lab quick navigation"
        className="fixed bottom-5 left-1/2 z-40 flex -translate-x-1/2 flex-row gap-1 rounded-2xl border border-[#d91616]/30 bg-black/60 p-2 backdrop-blur-xl md:bottom-auto md:left-5 md:top-1/2 md:-translate-x-0 md:-translate-y-1/2 md:flex-col md:gap-2"
      >
        {[
          { id: "home", label: "Home", Icon: Home, target: null },
          { id: "arsenal", label: "Arsenal", Icon: Crosshair, target: "products" },
          { id: "inquire", label: "Inquire", Icon: Send, target: "contact" },
        ].map(({ id, label, Icon, target }) => (
          <button
            key={id}
            data-testid={`lab-rail-${id}`}
            onClick={() => leaveTo(target)}
            className="group flex w-16 flex-col items-center gap-1 rounded-xl px-2 py-2.5 text-white/60 transition-colors hover:bg-[#d91616]/15 hover:text-white"
          >
            <Icon size={18} className="transition-transform group-hover:-translate-y-0.5" />
            <span className="font-mono2 text-[8px] uppercase tracking-[0.15em]">{label}</span>
          </button>
        ))}
      </nav>

      {/* Katana ink-wipe on back navigation */}
      <AnimatePresence>
        {wiping && (
          <motion.div
            key="lab-wipe"
            className="fixed inset-0 z-[100] origin-left"
            style={{
              background:
                "linear-gradient(90deg,#050505 0%,#3a0000 55%,#d91616 78%,#ff2d2d 100%)",
            }}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.55, ease: [0.7, 0, 0.3, 1] }}
            onAnimationComplete={() => navigate("/")}
            aria-hidden="true"
          >
            <span className="section-kanji font-jp absolute inset-0 grid place-items-center text-[30vw] leading-none text-black/20">
              藤
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
